import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';
import InfoIcon from '@mui/icons-material/Info';
import PermissionWrapper from '../components/PermissionWrapper';
import {
  listRules,
  createRule,
  updateRule,
  deleteRule,
  triggerFeedGeneration,
  getFeedUrl,
} from '../services/googleSupplementalService';
import type {
  SupplementalRule,
  SupplementalRulePayload,
  SupplementalRuleScope,
  SupplementalConditionOperator,
  SupplementalConditionField,
} from '../types/googleSupplemental';

const SCOPE_OPTIONS: { value: SupplementalRuleScope; label: string }[] = [
  { value: 'product', label: 'Artikel (alla färger och storlekar)' },
  { value: 'color', label: 'Färg (per färg, alla storlekar)' },
];

const CONDITION_FIELD_OPTIONS: {
  value: SupplementalConditionField;
  label: string;
}[] = [
  { value: 'stock', label: 'Totalt lagersaldo' },
  { value: 'number_of_sizes', label: 'Antal storlekar' },
  { value: 'sizes_with_stock', label: 'Antal storlekar med lager' },
  { value: 'min_stock_per_size', label: 'Minsta lager per storlek' },
  { value: 'stock_coverage', label: 'Stock coverage (gruppnivå)' },
];

const OPERATOR_OPTIONS: {
  value: SupplementalConditionOperator;
  label: string;
  short: string; // t.ex. "<" för listvyn
}[] = [
  { value: 'lt', label: 'Mindre än', short: '<' },
  { value: 'lte', label: 'Mindre än eller lika med', short: '≤' },
  { value: 'eq', label: 'Lika med', short: '=' },
  { value: 'gt', label: 'Större än', short: '>' },
  { value: 'gte', label: 'Större än eller lika med', short: '≥' },
];

const CUSTOM_LABEL_INDEX_OPTIONS = [0, 1, 2, 3, 4];

function getConditionFieldLabel(field: SupplementalConditionField): string {
  return CONDITION_FIELD_OPTIONS.find((o) => o.value === field)?.label ?? field;
}

function getOperatorShort(op: SupplementalConditionOperator): string {
  return OPERATOR_OPTIONS.find((o) => o.value === op)?.short ?? op;
}

/** Operatorfras för antal (färre/fler än) vs övriga (mindre/större än). */
function getOperatorPhraseForCondition(
  op: SupplementalConditionOperator,
  isCount: boolean,
): string {
  if (isCount) {
    switch (op) {
      case 'lt':
        return 'färre än';
      case 'lte':
        return 'högst';
      case 'eq':
        return 'exakt';
      case 'gt':
        return 'fler än';
      case 'gte':
        return 'minst';
      default:
        return OPERATOR_OPTIONS.find((o) => o.value === op)?.label ?? op;
    }
  }
  return OPERATOR_OPTIONS.find((o) => o.value === op)?.label ?? op;
}

/** Dynamisk förklaring av vad regeln gör (1–2 meningar). */
function getRuleExplanation(
  scope: SupplementalRuleScope,
  conditionField: SupplementalConditionField,
  conditionOperator: SupplementalConditionOperator,
  conditionValue: number,
  customLabelIndex: number,
  customLabelValue: string,
  colorFilter?: string | null,
  customLabelValueOk?: string | null,
): string {
  if (conditionField === 'stock_coverage') {
    const badVal = customLabelValue || 'bad_stock';
    const okVal = customLabelValueOk ?? 'ok';
    const label = `custom_label_${customLabelIndex}`;
    if (scope === 'product') {
      return `Alla varianter under artikeln får ${label} = '${badVal}' om färre än MIN_OK(storlekar) storlekar har lager, annars '${okVal}'. Onesize räknas som 1 storlek.`;
    }
    const colorQualifier = colorFilter ? ` (endast färg ${colorFilter})` : '';
    return `Per färg${colorQualifier}: alla varianter i den färgen får ${label} = '${badVal}' om färgen har färre än MIN_OK(storlekar) storlekar med lager, annars '${okVal}'. Onesize räknas som 1 storlek.`;
  }

  const isCount =
    conditionField === 'number_of_sizes' ||
    conditionField === 'sizes_with_stock';
  const opPhrase = getOperatorPhraseForCondition(conditionOperator, isCount);
  const label = customLabelValue
    ? `custom_label_${customLabelIndex} = '${customLabelValue}'`
    : `custom_label_${customLabelIndex}`;
  const scopeIsArticle = scope === 'product';

  const valueStr = String(conditionValue);
  const conditionPhrase = `${opPhrase} ${valueStr}`;

  if (scopeIsArticle) {
    const subject = 'Alla SKUs under artikeln';
    let condition: string;
    switch (conditionField) {
      case 'stock':
        condition = `artikelns totala lagersaldo är ${conditionPhrase}`;
        break;
      case 'number_of_sizes':
        condition = `artikeln har ${conditionPhrase} storlekar`;
        break;
      case 'sizes_with_stock':
        condition = `artikeln har ${conditionPhrase} storlekar med lager`;
        break;
      case 'min_stock_per_size':
        condition = `den minsta lagersaldot bland storlekarna i artikeln är ${conditionPhrase}`;
        break;
      default:
        condition = `mätvärde ${conditionField} är ${conditionPhrase}`;
    }
    return `${subject} får ${label} om ${condition}.`;
  }

  const colorQualifier = colorFilter ? ` (endast färg ${colorFilter})` : '';
  const subject = `För varje färg${colorQualifier}`;
  let condition: string;
  switch (conditionField) {
    case 'stock':
      condition = `färgens totala lagersaldo är ${conditionPhrase}`;
      break;
    case 'number_of_sizes':
      condition = `färgen har ${conditionPhrase} storlekar`;
      break;
    case 'sizes_with_stock':
      condition = `färgen har ${conditionPhrase} storlekar med lager`;
      break;
    case 'min_stock_per_size':
      condition = `den minsta lagersaldot bland storlekarna i färgen är ${conditionPhrase}`;
      break;
    default:
      condition = `mätvärde ${conditionField} är ${conditionPhrase}`;
  }
  return `${subject}: om ${condition}, får alla SKUs i den färgen ${label}.`;
}

const emptyForm: SupplementalRulePayload = {
  name: '',
  scope: 'product',
  colorFilter: undefined,
  conditionField: 'stock',
  conditionOperator: 'lt',
  conditionValue: 0,
  customLabelIndex: 0,
  customLabelValue: '',
  customLabelValueOk: undefined,
  priority: 0,
  isActive: true,
};

const GoogleSupplementalFeed: React.FC = () => {
  const [rules, setRules] = useState<SupplementalRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SupplementalRulePayload>(emptyForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fetchRules = useCallback(async () => {
    try {
      const data = await listRules();
      setRules(data);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Kunde inte hämta regler',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (rule: SupplementalRule) => {
    setEditingId(rule.ruleId);
    setForm({
      name: rule.name,
      scope: rule.scope,
      colorFilter: rule.colorFilter ?? undefined,
      conditionField: rule.conditionField,
      conditionOperator: rule.conditionOperator,
      conditionValue: rule.conditionValue,
      customLabelIndex: rule.customLabelIndex,
      customLabelValue: rule.customLabelValue,
      customLabelValueOk: rule.customLabelValueOk ?? undefined,
      priority: rule.priority ?? 0,
      isActive: rule.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (
    field: keyof SupplementalRulePayload,
    value: unknown,
  ) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'conditionField' && value === 'stock_coverage') {
        next.conditionOperator = 'eq';
        next.conditionValue = 0;
        next.customLabelValueOk = next.customLabelValueOk ?? 'ok';
      }
      return next;
    });
  };

  const isStockCoverage = form.conditionField === 'stock_coverage';

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setSnackbar({ open: true, message: 'Namn krävs', severity: 'error' });
      return;
    }
    if (form.customLabelValue.length > 100) {
      setSnackbar({
        open: true,
        message: 'Custom label-värde får max vara 100 tecken',
        severity: 'error',
      });
      return;
    }
    if (
      isStockCoverage &&
      form.customLabelValueOk != null &&
      form.customLabelValueOk.length > 100
    ) {
      setSnackbar({
        open: true,
        message: 'Värde vid ok får max vara 100 tecken',
        severity: 'error',
      });
      return;
    }
    setSubmitLoading(true);
    try {
      const payload: SupplementalRulePayload = {
        ...form,
        colorFilter: form.scope === 'color' ? form.colorFilter || null : null,
        ...(isStockCoverage && {
          conditionOperator: 'eq' as const,
          conditionValue: 0,
          customLabelValueOk: form.customLabelValueOk ?? 'ok',
        }),
      };
      if (editingId) {
        await updateRule(editingId, payload);
        setSnackbar({
          open: true,
          message: 'Regel uppdaterad',
          severity: 'success',
        });
      } else {
        await createRule(payload);
        setSnackbar({
          open: true,
          message: 'Regel skapad',
          severity: 'success',
        });
      }
      fetchRules();
      handleCloseDialog();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Kunde inte spara regeln',
        severity: 'error',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRule(id);
      setSnackbar({
        open: true,
        message: 'Regel borttagen',
        severity: 'success',
      });
      fetchRules();
      setDeleteConfirmId(null);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Kunde inte ta bort regeln',
        severity: 'error',
      });
    }
  };

  const handleTriggerFeed = async () => {
    setTriggerLoading(true);
    try {
      const res = await triggerFeedGeneration();
      setSnackbar({
        open: true,
        message: res.message || 'Supplemental feed genererad',
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Kunde inte generera feed',
        severity: 'error',
      });
    } finally {
      setTriggerLoading(false);
    }
  };

  const feedUrl = getFeedUrl();

  const operatorShort = (op: SupplementalConditionOperator) =>
    getOperatorShort(op);

  return (
    <PermissionWrapper requiredPermissions={['manage_companies']}>
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Google Supplemental Feed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Regler styr vilka custom labels (g:custom_label_0–4) som sätts i
            Google Merchant Center. Feed-rader mappas på SKU via PLU (g:id).
            Huvudfeeden påverkas inte.
          </Typography>
        </Box>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Generera feed</Typography>
                <Tooltip title="Kör feed-generering nu. Feed körs annars max 1 gång/dygn (cron 04:00).">
                  <InfoIcon color="action" fontSize="small" />
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTriggerFeed}
                disabled={triggerLoading}
                startIcon={
                  triggerLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
              >
                {triggerLoading ? 'Kör...' : 'Kör feed-generering'}
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Feed-URL för Google Merchant Center (hämtar senast genererade
              XML):
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body2"
                component="code"
                sx={{
                  bgcolor: 'action.hover',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                }}
              >
                {feedUrl}
              </Typography>
              <Button
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => window.open(feedUrl, '_blank')}
              >
                Öppna
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Regler</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Ny regel
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : rules.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              Inga regler. Skapa en regel för att styra custom labels.
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Namn</TableCell>
                  <TableCell>Nivå</TableCell>
                  <TableCell>Färgfilter</TableCell>
                  <TableCell>Mätvärde</TableCell>
                  <TableCell>Villkor</TableCell>
                  <TableCell>Custom label</TableCell>
                  <TableCell>Prioritet</TableCell>
                  <TableCell>Aktiv</TableCell>
                  <TableCell align="right">Åtgärder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.ruleId}>
                    <TableCell>{rule.name}</TableCell>
                    <TableCell>
                      {rule.scope === 'product' ? 'Artikel' : 'Färg'}
                    </TableCell>
                    <TableCell>
                      {rule.scope === 'color'
                        ? rule.colorFilter || 'Alla färger'
                        : '–'}
                    </TableCell>
                    <TableCell>
                      {getConditionFieldLabel(rule.conditionField)}
                    </TableCell>
                    <TableCell>
                      {rule.conditionField === 'stock_coverage'
                        ? 'bad / ok'
                        : `${operatorShort(rule.conditionOperator)} ${rule.conditionValue}`}
                    </TableCell>
                    <TableCell>
                      g:custom_label_{rule.customLabelIndex}{' '}
                      {rule.conditionField === 'stock_coverage'
                        ? `= "${rule.customLabelValue}" / "${rule.customLabelValueOk ?? 'ok'}"`
                        : `= "${rule.customLabelValue}"`}
                    </TableCell>
                    <TableCell>{rule.priority ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={rule.isActive !== false ? 'Ja' : 'Nej'}
                        size="small"
                        color={rule.isActive !== false ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(rule)}
                        aria-label="Redigera"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteConfirmId(rule.ruleId)}
                        aria-label="Ta bort"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingId ? 'Redigera regel' : 'Ny regel'}</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              <TextField
                label="Namn"
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                fullWidth
                placeholder="t.ex. Lågt lager färg"
              />
              <FormControl fullWidth>
                <InputLabel>Nivå</InputLabel>
                <Select
                  value={form.scope}
                  label="Nivå"
                  onChange={(e) =>
                    handleFormChange(
                      'scope',
                      e.target.value as SupplementalRuleScope,
                    )
                  }
                >
                  {SCOPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
                {isStockCoverage && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Artikel = hela artikeln; Färg = per färg (t.ex. Röd=ok,
                    Svart=bad_stock).
                  </Typography>
                )}
              </FormControl>
              {form.scope === 'color' && (
                <TextField
                  label="Färgfilter (färgkod / attribute1Code)"
                  value={form.colorFilter ?? ''}
                  onChange={(e) =>
                    handleFormChange('colorFilter', e.target.value || undefined)
                  }
                  fullWidth
                  placeholder="Lämna tomt för alla färger"
                />
              )}
              <FormControl fullWidth>
                <InputLabel>Mätvärde (conditionField)</InputLabel>
                <Select
                  value={form.conditionField}
                  label="Mätvärde (conditionField)"
                  onChange={(e) =>
                    handleFormChange(
                      'conditionField',
                      e.target.value as SupplementalConditionField,
                    )
                  }
                >
                  {CONDITION_FIELD_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isStockCoverage && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ flex: '1 1 200px' }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={form.conditionOperator}
                      label="Operator"
                      onChange={(e) =>
                        handleFormChange(
                          'conditionOperator',
                          e.target.value as SupplementalConditionOperator,
                        )
                      }
                    >
                      {OPERATOR_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Värde"
                    type="number"
                    value={form.conditionValue}
                    onChange={(e) =>
                      handleFormChange('conditionValue', Number(e.target.value))
                    }
                    inputProps={{ min: 0 }}
                    sx={{ flex: '1 1 120px' }}
                  />
                </Box>
              )}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Så fungerar regeln:
                </Typography>
                <Typography variant="body2">
                  {getRuleExplanation(
                    form.scope,
                    form.conditionField,
                    form.conditionOperator,
                    form.conditionValue,
                    form.customLabelIndex,
                    form.customLabelValue,
                    form.colorFilter,
                    form.customLabelValueOk,
                  )}
                </Typography>
              </Paper>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Custom label slot</InputLabel>
                  <Select
                    value={form.customLabelIndex}
                    label="Custom label slot"
                    onChange={(e) =>
                      handleFormChange(
                        'customLabelIndex',
                        Number(e.target.value),
                      )
                    }
                  >
                    {CUSTOM_LABEL_INDEX_OPTIONS.map((i) => (
                      <MenuItem key={i} value={i}>
                        custom_label_{i}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isStockCoverage ? (
                  <>
                    <TextField
                      label="Värde vid dålig täckning (max 100 tecken)"
                      value={form.customLabelValue}
                      onChange={(e) =>
                        handleFormChange('customLabelValue', e.target.value)
                      }
                      fullWidth
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 100 }}
                      placeholder="t.ex. bad_stock"
                    />
                    <TextField
                      label="Värde vid ok (max 100 tecken)"
                      value={form.customLabelValueOk ?? 'ok'}
                      onChange={(e) =>
                        handleFormChange(
                          'customLabelValueOk',
                          e.target.value || undefined,
                        )
                      }
                      fullWidth
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 100 }}
                      placeholder="ok"
                      helperText="Default: ok"
                    />
                  </>
                ) : (
                  <TextField
                    label="Värde (max 100 tecken)"
                    value={form.customLabelValue}
                    onChange={(e) =>
                      handleFormChange('customLabelValue', e.target.value)
                    }
                    fullWidth
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 100 }}
                    placeholder="t.ex. Bad"
                  />
                )}
              </Box>
              <TextField
                label="Prioritet"
                type="number"
                value={form.priority ?? 0}
                onChange={(e) =>
                  handleFormChange('priority', Number(e.target.value))
                }
                inputProps={{ min: 0 }}
                helperText="Högre värde = högre prioritet (körs först)"
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) =>
                      handleFormChange('isActive', e.target.checked)
                    }
                  />
                }
                label="Aktiv"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Avbryt</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitLoading || !form.name.trim()}
            >
              {submitLoading ? 'Sparar...' : editingId ? 'Spara' : 'Skapa'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteConfirmId)}
          onClose={() => setDeleteConfirmId(null)}
        >
          <DialogTitle>Ta bort regel?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmId(null)}>Avbryt</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              Ta bort
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PermissionWrapper>
  );
};

export default GoogleSupplementalFeed;
